import React, { FC, useContext, useState, useEffect, useCallback, Fragment } from 'react';

import { AccordionDetails, AccordionSummary, Grid, Accordion } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Droppable } from 'react-beautiful-dnd';
import moment from 'moment';

import { IRoundGame, IRoundGroup } from '../../../../../../store/admin/eventRounds/store';
import { GlobalContext } from '../../../../../../store';

import {
  useTeeSheetCreatorDispatch,
  useTeeSheetCreatorState
} from '../../Context/TeeSheetCreator.context';
import EmptySlot from './Components/EmptySlot';
import { DraggableGroupStyled } from './DraggableGroupStyled';
import GroupGame from './GroupGame';
import { sortBy } from '../../../../../../contrib/sortBy';
import Select from '../../../../../../components/AutoComplete';
import { useShotgunOptions } from './useShotgunOptions';

interface IProps {
  group: IRoundGroup;
}

const Group: FC<IProps> = props => {
  const { group } = props;

  const {
    adminEventRounds: { current: round }
  } = useContext(GlobalContext);

  const {
    actions: { onSelectSeat },
    dispatch
  } = useTeeSheetCreatorDispatch();

  const { openGroups } = useTeeSheetCreatorState();

  const {
    onShotgunOptionSelected,
    shotgunOptionsFormatted,
    isShotgun,
    groupShotgunSpot
  } = useShotgunOptions(group);

  const [slots, setSlots] = useState(1);
  const [groupIsOpen, setGroupIsOpen] = useState(false);
  const [emptySlots, setEmptySlots] = useState(0);
  const [groupStartTime, setGroupStartTime] = useState('');
  const [droppableId, setDroppableId] = useState('');
  const [sortedGames, setSortedGames] = useState<IRoundGame[]>([]);
  const [isFull, setIsFull] = useState(false);

  const onToggle = useCallback(() => {
    dispatch({
      type: 'set',
      value: {
        attribute: 'openGroups',
        value: { ...openGroups, [group.id]: !!!openGroups[group.id] }
      }
    });
  }, [openGroups, group, dispatch]);

  const onSelect = useCallback(
    (seatId: number) => () => {
      onSelectSeat(group.id)(seatId)();
    },
    [onSelectSeat, group]
  );

  useEffect(() => {
    setDroppableId(String(group.id));
    setGroupStartTime(moment(group.start).format('hh:mm A'));
  }, [group]);

  useEffect(() => {
    if (round && round.playersPerTee) {
      setSlots(round.playersPerTee);
    }
  }, [round]);

  useEffect(() => {
    if (!!group.games && group.games.length <= slots) {
      setEmptySlots(slots - group.games.length);
    }
  }, [slots, group.games.length]);

  useEffect(() => {
    setGroupIsOpen(!!openGroups[group.id]);
  }, [openGroups, group]);

  useEffect(() => {
    const newSortedGames = sortBy(group.games, 'asc', 'seat');

    setSortedGames(newSortedGames);
  }, [group]);

  useEffect(() => {
    setIsFull(group.games.length === slots);
  }, [group, slots]);

  return droppableId ? (
    <Droppable droppableId={droppableId} key={`Group-Droppable-${group.id}`}>
      {(provided: any, snapshot: any) => (
        <DraggableGroupStyled
          key={`Group-Styled-${group.id}`}
          ref={provided.innerRef}
          isDraggingOver={snapshot.isDraggingOver}
          isFull={isFull}
        >
          <Accordion expanded={groupIsOpen} className="group-panel">
            <AccordionSummary
              expandIcon={<ExpandMoreIcon onClick={onToggle} />}
              className="group-header"
            >
              {isShotgun ? (
                <Select
                  defaultValue={groupShotgunSpot}
                  placeholder="Select"
                  onChange={onShotgunOptionSelected}
                  color="primary"
                  options={shotgunOptionsFormatted}
                />
              ) : (
                groupStartTime
              )}

              <div className="circle-slots">
                {[...Array(slots)].fill('empty').map((_, index) => (
                  <div
                    className={`circle-slot ${group.games.length > index ? 'filled' : ''}`}
                    key={`circle-slot-${index}`}
                  />
                ))}
              </div>
            </AccordionSummary>

            <AccordionDetails className="expansion-panel-details individual">
              <Grid className="navigator" container={true} spacing={2}>
                {sortedGames.map((game, index) => (
                  <Grid item={true} xs={6} key={`group-grid-${game.id}`}>
                    <GroupGame game={game} index={index} />
                  </Grid>
                ))}

                {[...Array(emptySlots)].map((_, index) => (
                  <Grid item={true} xs={6} key={`emty-slot-grid-${index}`}>
                    <EmptySlot onSelectSlot={onSelect(index * 2 + Number(1))} />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </DraggableGroupStyled>
      )}
    </Droppable>
  ) : (
    <Fragment key={`Group-Droppable-${group.id}`} />
  );
};

export default Group;
